import React, { useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { iso } from "./npdUtils";
import { errorMessage, npdApi } from "./npdApi";

const MAX_ROWS = 500;

// Columns of the Parts sheet. Buyer Email is only in the admin's template.
const COLUMNS = [
  { key: "lineCode", header: "Assembly Line Code" },
  { key: "partName", header: "Part Name" },
  { key: "supplier", header: "Supplier" },
  { key: "targetDate", header: "Target Date (DD-MM-YYYY)" },
  { key: "stage", header: "Current Stage (number or name)" },
  { key: "buyerEmail", header: "Buyer Email", adminOnly: true },
  { key: "note", header: "Note" },
];

// Case-insensitive key that ignores leading, trailing and repeated spaces (same rule as the server).
const matchKey = (value) => String(value ?? "").trim().replace(/\s+/g, " ").toLowerCase();

const text = (value) => String(value ?? "").trim();

// Excel date, "DD-MM-YYYY", "DD/MM/YYYY" or "YYYY-MM-DD" -> "YYYY-MM-DD"; null if not a valid date.
const toIsoDate = (value) => {
  if (value === null || value === undefined || value === "") return null;
  let y;
  let m;
  let d;
  if (typeof value === "number") {
    const parsed = XLSX.SSF.parse_date_code(value);
    if (!parsed) return null;
    ({ y, m, d } = parsed);
  } else if (value instanceof Date && !Number.isNaN(value.getTime())) {
    y = value.getFullYear();
    m = value.getMonth() + 1;
    d = value.getDate();
  } else {
    const s = text(value);
    let match = s.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2}|\d{4})$/);
    if (match) {
      [, d, m, y] = match.map(Number);
      if (y < 100) y += 2000;
    } else {
      match = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
      if (!match) return null;
      [, y, m, d] = match.map(Number);
    }
  }
  const date = new Date(y, m - 1, d);
  if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d) return null;
  return iso(date);
};

// Stage number (1 = first) or stage name of the line; blank is the first stage. Returns the index or null.
const stageIndex = (line, value) => {
  const s = text(value);
  if (!s) return 0;
  if (/^\d+(\.0+)?$/.test(s)) {
    const n = Math.floor(Number(s));
    return n >= 1 && n <= line.stages.length ? n - 1 : null;
  }
  const i = line.stages.findIndex((stage) => matchKey(stage) === matchKey(s));
  return i >= 0 ? i : null;
};

// Excel import for many parts at once. Uses the same rules as the server, which checks the file
// again and saves nothing unless every row is valid.
const ImportParts = ({ app }) => {
  const { isAdmin, lines, suppliers, buyers, parts, today, onPartSaved, onSupplierAdded, toast } = app;
  const fileRef = useRef(null);
  const [fileName, setFileName] = useState("");
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState("");
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  const columns = COLUMNS.filter((c) => isAdmin || !c.adminOnly);

  const downloadTemplate = () => {
    const wb = XLSX.utils.book_new();

    const partsSheet = XLSX.utils.aoa_to_sheet([columns.map((c) => c.header)]);
    partsSheet["!cols"] = columns.map((c) => ({ wch: Math.max(16, c.header.length + 2) }));
    // Target Date cells are text, so Excel keeps "10-12-2026" as typed instead of turning it
    // into a date with the computer's month/day order.
    const dateCol = columns.findIndex((c) => c.key === "targetDate");
    for (let r = 1; r <= MAX_ROWS; r++) {
      partsSheet[XLSX.utils.encode_cell({ r, c: dateCol })] = { t: "s", v: "", z: "@" };
    }
    partsSheet["!ref"] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: MAX_ROWS, c: columns.length - 1 } });
    XLSX.utils.book_append_sheet(wb, partsSheet, "Parts");

    // Reference values to copy from: each line's stages, the suppliers and (for the admin) the buyers
    const ref = [["Assembly lines and their stages"], ["Line Code", "Line Name", "Product", "Stage No", "Stage Name"]];
    lines.forEach((l) => l.stages.forEach((s, i) => ref.push([l.code, l.name, l.product, i + 1, s])));
    ref.push([], ["Suppliers (a name not in this list is added as a new supplier)"]);
    suppliers.forEach((s) => ref.push([s.name]));
    if (isAdmin) {
      ref.push([], ["Buyers", "Buyer Email"]);
      buyers.forEach((b) => ref.push([b.name, b.email]));
    }
    const refSheet = XLSX.utils.aoa_to_sheet(ref);
    refSheet["!cols"] = [{ wch: 22 }, { wch: 28 }, { wch: 22 }, { wch: 10 }, { wch: 40 }];
    XLSX.utils.book_append_sheet(wb, refSheet, "Reference");

    XLSX.writeFile(wb, "NPD_Parts_Import_Template.xlsx");
  };

  const clear = () => {
    setRows([]);
    setFileName("");
    setErr("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const readFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setErr("");
    setResult(null);
    setRows([]);
    try {
      if (!/\.(xlsx|xls)$/i.test(file.name)) throw new Error("Choose an Excel file (.xlsx or .xls).");
      const wb = XLSX.read(await file.arrayBuffer(), { type: "array" });
      const sheet = wb.Sheets.Parts || wb.Sheets[wb.SheetNames[0]];
      const data = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "", raw: true });
      // The same cells as shown in Excel, used for dates Excel has already converted
      const shown = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "", raw: false });
      if (!data.length) throw new Error("The Parts sheet is empty.");

      const headers = data[0].map((h) => matchKey(h));
      const index = {};
      columns.forEach((c) => {
        index[c.key] = headers.indexOf(matchKey(c.header));
      });
      const missing = columns.filter((c) => index[c.key] < 0 && c.key !== "note").map((c) => c.header);
      if (missing.length) {
        throw new Error(`Missing column(s): ${missing.join(", ")}. Download the template to get the exact headings.`);
      }

      const read = data
        .map((r, i) => ({ r, rowNumber: i + 1 }))
        .slice(1)
        .filter(({ r }) => r.some((v) => text(v) !== ""))
        .map(({ r, rowNumber }) => {
          const cell = (key) => (index[key] >= 0 ? r[index[key]] : "");
          // If Excel turned a typed "10-12-2026" into a date, it may have read it month-first.
          // Read it as shown in the cell, as DD-MM-YYYY like the column heading says.
          const dateShown = index.targetDate >= 0 ? text(shown[rowNumber - 1]?.[index.targetDate]) : "";
          const dateValue =
            typeof cell("targetDate") === "number" && /^\d{1,2}[-/.]\d{1,2}[-/.](\d{2}|\d{4})$/.test(dateShown)
              ? dateShown
              : cell("targetDate");
          return {
            rowNumber,
            lineCode: text(cell("lineCode")),
            partName: text(cell("partName")),
            supplier: text(cell("supplier")),
            targetDateRaw: dateValue,
            targetDate: toIsoDate(dateValue),
            stage: text(cell("stage")),
            buyerEmail: text(cell("buyerEmail")),
            note: text(cell("note")),
          };
        });
      if (!read.length) throw new Error("The Parts sheet has no parts.");
      if (read.length > MAX_ROWS) throw new Error(`Import up to ${MAX_ROWS} parts at a time (the file has ${read.length}).`);

      setFileName(file.name);
      setRows(read);
    } catch (ex) {
      setErr(ex.message || "Could not read the Excel file.");
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  // Each row checked with the same rules as the server, plus warnings that do not block the import
  const checked = useMemo(() => {
    const linesByCode = new Map(lines.map((l) => [matchKey(l.code), l]));
    const supplierKeys = new Set(suppliers.map((s) => matchKey(s.name)));
    const buyersByEmail = new Map(buyers.map((b) => [matchKey(b.email), b]));
    const seen = new Set();

    return rows.map((row) => {
      const errors = [];
      const warnings = [];
      const line = row.lineCode ? linesByCode.get(matchKey(row.lineCode)) : null;
      if (!row.lineCode) errors.push("Assembly Line Code is required");
      else if (!line) errors.push(`Assembly line "${row.lineCode}" does not exist`);

      if (!row.partName) errors.push("Part Name is required");
      else if (row.partName.length > 255) errors.push("Part Name is too long");

      if (!row.supplier) errors.push("Supplier is required");
      else if (row.supplier.length > 255) errors.push("Supplier is too long");

      if (!text(row.targetDateRaw)) errors.push("Target Date is required");
      else if (!row.targetDate) errors.push(`Target Date "${text(row.targetDateRaw)}" is not a valid date`);
      else if (row.targetDate < iso(today)) errors.push(`Target Date ${row.targetDate} is in the past`);

      let stage = 0;
      if (line) {
        const i = stageIndex(line, row.stage);
        if (i === null) errors.push(`Stage "${row.stage}" is not a stage of line ${line.code} (use 1 to ${line.stages.length} or the stage name)`);
        else stage = i;
      }

      let buyer = null;
      if (isAdmin) {
        if (!row.buyerEmail) errors.push("Buyer Email is required");
        else {
          buyer = buyersByEmail.get(matchKey(row.buyerEmail));
          if (!buyer) errors.push(`"${row.buyerEmail}" is not an active buyer of your organization`);
        }
      }

      if (line && row.partName) {
        const key = `${line.id}|${matchKey(row.partName)}`;
        if (seen.has(key)) warnings.push("Same part name appears earlier in the file for this line");
        else if (parts.some((p) => p.L.id === line.id && matchKey(p.name) === matchKey(row.partName))) {
          warnings.push("A part with this name already exists on this line");
        }
        seen.add(key);
      }

      return {
        ...row,
        line,
        stageRaw: row.stage,
        stage,
        stageName: line?.stages[stage],
        buyer,
        newSupplier: row.supplier && !supplierKeys.has(matchKey(row.supplier)),
        errors,
        warnings,
      };
    });
  }, [rows, lines, suppliers, buyers, parts, isAdmin, today]);

  const errorCount = checked.filter((r) => r.errors.length).length;
  const newSupplierNames = [...new Map(checked.filter((r) => r.newSupplier).map((r) => [matchKey(r.supplier), r.supplier])).values()];

  const submit = async () => {
    if (!checked.length || errorCount) return;
    setImporting(true);
    setErr("");
    try {
      const res = await npdApi.importParts({
        fileName,
        rows: checked.map((r) => ({
          rowNumber: r.rowNumber,
          lineCode: r.lineCode,
          partName: r.partName,
          supplier: r.supplier,
          targetDate: r.targetDate,
          // Stage already resolved for this line; sent as its number (1 = first stage)
          stage: String(r.stage + 1),
          buyerEmail: isAdmin ? r.buyerEmail : undefined,
          note: r.note,
        })),
      });
      (res.newSuppliers || []).forEach(onSupplierAdded);
      (res.parts || []).forEach(onPartSaved);
      setResult(res);
      toast(`${res.parts?.length || 0} part(s) imported`);
      clear();
    } catch (ex) {
      setErr(errorMessage(ex, "Could not import the parts."));
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="panel">
      <h3>Import parts from Excel</h3>
      <p className="mut" style={{ margin: "0 0 10px" }}>
        Download the template, fill one part per row in the Parts sheet and upload it. The Reference sheet lists each
        line's code and stages{isAdmin ? ", the suppliers and the buyers" : " and the suppliers"}. Part numbers are
        created automatically. A supplier not in the list is added as a new supplier. Nothing is imported until every row is valid.
      </p>
      <div className="tools">
        <button type="button" className="btn" onClick={downloadTemplate}>Download template</button>
        {/* File inputs are hidden app-wide (App.css), so a button opens the file picker */}
        <input ref={fileRef} type="file" hidden accept=".xlsx,.xls" onChange={readFile} />
        <button type="button" className="btn p" onClick={() => fileRef.current?.click()}>Upload Excel file</button>
        {rows.length > 0 && <button type="button" className="btn" onClick={clear}>Clear</button>}
      </div>

      {result && (
        <div className="panel ok" style={{ marginTop: 10 }}>
          Imported {result.parts.length} part(s)
          {result.parts.length > 0 && ` (${result.parts[0].partNo}${result.parts.length > 1 ? ` to ${result.parts[result.parts.length - 1].partNo}` : ""})`}
          {result.newSuppliers.length > 0 && `; added ${result.newSuppliers.length} new supplier(s): ${result.newSuppliers.map((s) => s.name).join(", ")}`}.
        </div>
      )}

      <div className="red" style={{ marginTop: 8, whiteSpace: "pre-line" }}>{err}</div>

      {checked.length > 0 && (
        <>
          <p style={{ margin: "10px 0" }}>
            <b>{fileName}</b>: {checked.length} part(s),{" "}
            {errorCount ? <span className="red">{errorCount} row(s) with errors</span> : "all rows valid"}
            {newSupplierNames.length > 0 && <> · {newSupplierNames.length} new supplier(s): {newSupplierNames.join(", ")}</>}
          </p>
          <div className="wrap">
            <table>
              <thead>
                <tr>
                  <th>Row</th><th>Line</th><th>Part name</th><th>Supplier</th><th>Target date</th><th>Starting stage</th>
                  {isAdmin && <th>Buyer</th>}<th>Check</th>
                </tr>
              </thead>
              <tbody>
                {checked.map((r) => (
                  <tr key={r.rowNumber}>
                    <td>{r.rowNumber}</td>
                    <td>{r.line ? `${r.line.code} · ${r.line.name}` : r.lineCode || "-"}</td>
                    <td>{r.partName || "-"}</td>
                    <td>{r.supplier || "-"}{r.newSupplier && <span className="mut"> (new)</span>}</td>
                    <td>{r.targetDate || text(r.targetDateRaw) || "-"}</td>
                    <td>{r.stageName ? `${r.stage + 1}. ${r.stageName}` : r.stageRaw || "-"}</td>
                    {isAdmin && <td>{r.buyer?.name || r.buyerEmail || "-"}</td>}
                    <td>
                      {r.errors.length ? (
                        <span className="red">{r.errors.join("; ")}</span>
                      ) : (
                        <>OK{r.warnings.length > 0 && <span className="mut"> · {r.warnings.join("; ")}</span>}</>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="tools" style={{ marginTop: 10 }}>
            <button type="button" className="btn p" onClick={submit} disabled={importing || errorCount > 0}>
              {importing ? "Importing..." : `Import ${checked.length} part(s)`}
            </button>
            {errorCount > 0 && <span className="red">Fix the rows with errors in the file and upload it again.</span>}
          </div>
        </>
      )}
    </div>
  );
};

export default ImportParts;
